import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css } from 'emotion';
import { camelCase, sortBy } from 'lodash';
import { computed, extendObservable, observable, set, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { BREAKPOINT_MEDIUM } from '../consts/breakpoints';
import { APP_DATA_TOKEN, I18N_TOKEN } from '../services/InjectTokens';
import { Avatar, Field, OptionValue } from '../state/models';
import { Button } from './Button';
import { FormSelectors } from './FormSelectors';
import { Input } from './Input';

const input = css`
  padding: 20px 0;
`;

const wrapper = css`
  display: flex;
  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    margin-top: 40px;
    margin-bottom: 40px;
    flex-direction: column-reverse;
  }
`;

const form = css`
  flex: 1;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    padding: 0 20px;
  }
`;

const save = css`
  text-align: right;
  padding: 0;
  margin-top: 20px;
  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    padding: 0 20px;
  }
`;

const title = css`
  text-align: center;
`;

interface IFormProps {
  bookId?: number;
  avatar: Avatar;
  defaults: Record<string, string>;
  children?: React.ReactNode;
  elementsAfter: React.ReactNode;
  onSave(avatar: Avatar, data: Record<string, string>, mainCharacter?: boolean): void;
  onFeatureChange(params?: any): void;
}

interface IComponentState {
  selectedOption: string | null;
  showingErrors: boolean;
  defaults: Record<string, string>;
}

@observer
export class Form extends React.Component<IFormProps> {
  private i18n = inject(I18N_TOKEN);
  private appState = inject(APP_DATA_TOKEN);

  @observable
  private formState: Record<string, string> = {};

  @observable
  private componentState: IComponentState = {
    defaults: this.props.defaults,
    selectedOption: this.props.avatar.features.length ? this.props.avatar.features[0].icon : null,
    showingErrors: false,
  };

  constructor(props: IFormProps) {
    super(props);
    const { avatar } = this.props;

    extendObservable(
      this.formState,
      avatar.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.uid]: avatar.params ? avatar.params[field.uid] : field.mandatory ? '' : ' ',
        }),
        {},
      ),
    );
  }

  @computed
  private get fields() {
    return sortBy(this.props.avatar.fields, 'position');
  }

  @computed
  private get features() {
    if (!this.props.avatar.features.length) {
      return [];
    }

    return this.props.avatar.features.map((feature) => {
      return {
        feature,
        isSelected: feature.icon === this.componentState.selectedOption,
      };
    });
  }

  @computed get pickers() {
    const activeFeature = this.features.find(({ feature }) => feature.icon === this.componentState.selectedOption);
    if (activeFeature) {
      return activeFeature.feature.pickers;
    }

    return null;
  }

  public render() {
    const { bookId, avatar, children } = this.props;

    return (
      <React.Fragment>
        <div className={wrapper}>
          <div className={form}>
            <h3 className={title}>{this.i18n.t(`customize_title_${this.props.avatar.uid}`)}</h3>
            {Boolean(this.features.length) && (
              <FormSelectors
                bookId={bookId}
                avatar={avatar}
                features={this.features}
                pickers={this.pickers}
                params={{
                  ...this.props.defaults,
                  ...this.props.avatar.params,
                }}
                avatarGender={this.props.avatar.gender}
                onPickerSelect={this.onPickerSelect}
                onOptionSelect={this.onOptionSelect}
              />
            )}
            {this.fields
              .filter((field) => field.inLanguages.includes(this.appState.currentLanguage))
              .map((field) => (
                <Input
                  key={field.uid}
                  value={this.formState[field.uid]}
                  name={field.name}
                  onChange={this.onChange(field.uid)}
                  errorValue={this.computeError(field, this.componentState.showingErrors)}
                  className={input}
                  maxLength={field.maxLength}
                />
              ))}
            {this.props.elementsAfter}
          </div>
          {children}
        </div>
        <Button value={this.i18n.t('next_tab')} onClick={this.onSave} className={save} />
      </React.Fragment>
    );
  }

  @bind
  private onOptionSelect(icon: string) {
    this.componentState.selectedOption = icon;
  }

  @bind
  private onPickerSelect(uid: string, value: OptionValue) {
    return () => {
      this.props.onFeatureChange({ uid, value });
    };
  }

  @bind
  private onChange(uid: string) {
    return (e: React.ChangeEvent<HTMLInputElement>) => set(this.formState, uid, e.target.value);
  }

  @bind
  private onSave() {
    const { avatar, onSave } = this.props;

    const isFormValid = avatar.fields
      .filter((field) => field.inLanguages.includes(this.appState.currentLanguage))
      .every((field) => Boolean(this.formState[field.uid]));

    this.componentState.showingErrors = true;
    if (isFormValid) {
      onSave(
        avatar,
        {
          ...avatar.params,
          ...toJS(this.formState),
        },
        avatar.mainCharacter,
      );
    }
  }

  @bind
  private computeError(field: Field, lazy = false) {
    if (lazy && field.mandatory && !this.formState[field.uid]) {
      const { translations, currentLanguage } = this.appState;

      return translations[currentLanguage][`${camelCase(field.uid)}ErrorMessage`];
    }

    return '';
  }
}
